import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { ElementRef, Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationExtras, QueryParamsHandling, Router } from '@angular/router';
import {
  AppearanceAnimation,
  DialogLayoutDisplay,
  DisappearanceAnimation,
  ToastNotificationInitializer,
  ToastPositionEnum,
  ToastProgressBarEnum,
  ToastUserViewTypeEnum
} from '@costlydeveloper/ngx-awesome-popup';
import { DsAuthJWTToken } from '@direction/auth';
import { ResourceService } from '@direction/translate';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CepAddress } from '../models/cep.address.model';
import { GlobalUser } from '../models/global.user.model';

@Injectable({
  providedIn: 'root'
})
export class HelpersService {
  constructor(
    private http: HttpClient,
    private handler: HttpBackend,
    private resource: ResourceService,
    private sanitizer: DomSanitizer
  ) {}

  getAddressFromZipCode(zipCode: string): Observable<CepAddress> {
    this.http = new HttpClient(this.handler);
    return this.http.get<CepAddress>(`https://brasilapi.com.br/api/cep/v2/${zipCode}`);
  }

  getDsAuthJWTToken(): DsAuthJWTToken {
    return new DsAuthJWTToken();
  }

  getLoggedUser(): GlobalUser {
    try {
      const user = JSON.parse(this.getDsAuthJWTToken().getPayload().user) as GlobalUser;
      return user;
    } catch {
      return new GlobalUser();
    }
  }

  /**
   * Get headers para request
   * { Authorization: `Bearer token....` };
   */
  getHeadersAuth(): any {
    return { Authorization: `Bearer ${this.getDsAuthJWTToken().getValue()}` };
  }

  /**
   * Reduz a imagem para o formato especificado
   * @param file
   * @param MAX_WIDTH
   * @param MAX_HEIGHT
   */

  compress(file: File, MAX_WIDTH: number = 200, MAX_HEIGHT: number = 200): Observable<any> {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    return Observable.create((observer: any) => {
      reader.onload = (ev) => {
        const img = new Image();

        img.src = (ev.target as any).result;

        (img.onload = () => {
          const elem = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;

              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;

              height = MAX_HEIGHT;
            }
          }
          elem.width = width;
          elem.height = height;
          const ctx = elem.getContext('2d') as CanvasRenderingContext2D;
          ctx.drawImage(img, 0, 0, width, height);
          ctx.canvas.toBlob(
            (blob) => {
              observer.next(
                new File([blob as BlobPart], file.name, {
                  type: file.type,

                  lastModified: Date.now()
                })
              );
            },
            file.type,
            1
          );
        }),
          (reader.onerror = (error) => observer.error(error));
      };
    });
  }

  completedBase64(base64?: string) {
    var prefix = '/assets/images/noPicture.webp';
    if (!base64) return prefix;
    if (base64.startsWith('/9j/4')) prefix = 'data:image/jpg;base64,';
    if (base64.startsWith('iVBOR')) prefix = 'data:image/png;base64,';
    if (base64.startsWith('UklGR')) prefix = 'data:image/octet-stream;base64,';
    if (base64.startsWith('JVBER')) prefix = 'data:application/pdf;base64,';
    return prefix + base64;
  }

  validateCPF(control: AbstractControl) {
    // TODO: Colocar esse validador na @direction/forms
    if (control.value) {
      const cpf = control.value.replaceAll('.', '').replaceAll('-', '');
      if (cpf) {
        let numbers: { charAt: (arg0: number) => number };
        let digits: string;
        let sum: number;
        let i: number;
        let result: number;
        let equalDigits: number;
        equalDigits = 1;
        if (cpf.length !== 11) {
          return { custom: true };
        }

        for (i = 0; i < cpf.length - 1; i++) {
          if (cpf.charAt(i) !== cpf.charAt(i + 1)) {
            equalDigits = 0;
            break;
          }
        }

        if (!equalDigits) {
          numbers = cpf.substring(0, 9);
          digits = cpf.substring(9);
          sum = 0;
          for (i = 10; i > 1; i--) {
            sum += numbers.charAt(10 - i) * i;
          }

          result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

          if (result !== Number(digits.charAt(0))) {
            return { custom: true };
          }
          numbers = cpf.substring(0, 10);
          sum = 0;

          for (i = 11; i > 1; i--) {
            sum += numbers.charAt(11 - i) * i;
          }
          result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

          if (result !== Number(digits.charAt(1))) {
            return { custom: true };
          }
          return null;
        } else {
          return { custom: true };
        }
      }
      return null;
    }
    return null;
  }

  validateCNPJ(control: AbstractControl) {
    const cnpj = control.value;

    if (cnpj) {
      const multiplicador1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      const multiplicador2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

      // Esta função retira os caracteres '.', '/', '-' da string do CNPJ, deixando apenas os números
      const strCNPJ = cnpj.replace('.', '').replace('/', '').replace('-', '');

      if (strCNPJ.length !== 14) {
        return { custom: true };
      }

      if (isNaN(+strCNPJ)) {
        return { custom: true };
      }

      let tempCnpj: string = strCNPJ.substr(0, 12);
      let sum = 0;

      for (let i = 0; i < 12; i++) {
        sum += Number(tempCnpj.substr(i, 1)) * multiplicador1[i];
      }

      let rest = sum % 11;
      if (rest < 2) {
        rest = 0;
      } else {
        rest = 11 - rest;
      }
      let digit = rest.toString();
      tempCnpj = tempCnpj + digit;
      sum = 0;
      for (let i = 0; i < 13; i++) {
        sum += Number(tempCnpj.substr(i, 1)) * multiplicador2[i];
      }

      rest = sum % 11;
      if (rest < 2) {
        rest = 0;
      } else {
        rest = 11 - rest;
      }

      digit = digit + rest.toString();

      if (digit !== strCNPJ.substr(12, 2)) {
        return { custom: true };
      }
    }

    return null;
  }

  validateCelular(control: AbstractControl) {
    // Exemplo de formato válido: (81) 99992-1234
    const celular = control.value;

    if (celular) {
      // Esta função retira os caracteres '(', ')', '-', ' ' da string do celular, deixando apenas os números
      const strCelular = celular.replace('(', '').replace(')', '').replace(' ', '').replace('-', '');
      if (strCelular.length !== 11) {
        return { custom: true };
      }
      if (strCelular.substr(2, 1) !== '9') {
        return { custom: true };
      }
    }
    return null;
  }

  validateChaveAleatoria(control: AbstractControl) {
    const chaveAleatoria: string = control.value;

    if (chaveAleatoria) {
      if (!chaveAleatoria.match('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$')) {
        return { custom: true };
      }
    }
    return null;
  }

  // Força reload para a mesma rota apenas mudando parâmetros
  reloadTo(router: Router, uri: string, queryParams: NavigationExtras, queryParamsHandling: QueryParamsHandling = 'preserve') {
    router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() => router.navigate([uri], { queryParams, queryParamsHandling }));
  }

  isoToDate(date: any): string {
    return [date.substr(0, 4), date.substr(5, 2), date.substr(8, 2)].join('-');
  }

  // Formata data de AAAA-MM-DDTHH:MM:SS para DD/MM/AAAA
  isoToShortDate(data: string | any) {
    const dataArray = data.split('-');
    return dataArray[2].substr(0, 2) + '/' + dataArray[1] + '/' + dataArray[0];
  }

  // Formata data de DD/MM/AAAA ou DDMMAAAA para AAAA-MM-DD
  shortDateToISO(data: string) {
    const shortDate = data?.split('/').join('');
    return shortDate?.substr(4) + '-' + shortDate?.substr(2, 2) + '-' + shortDate?.substr(0, 2);
  }
  shortDateToISOBar(data: string) {
    const shortDate = data.split('/').join('');
    return shortDate.substr(4) + '/' + shortDate.substr(2, 2) + '/' + shortDate.substr(0, 2);
  }

  // Formata uma data do tipo Date ( ou se vier string DD/MM/AAAA) para AAAA-MM-DD
  dateToISO(d: any) {
    if (typeof d === 'string') {
      return this.shortDateToISO(d);
    }
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }
    return [year, month, day].join('-');
  }

  dateToddmmyyyy(dt: Date) {
    const y = dt.getFullYear();
    const d = dt.getDate();
    const m = dt.getMonth() + 1;
    const mm = m < 10 ? '0' + m : m;
    const dd = d < 10 ? '0' + d : d;
    return '' + dd + mm + y;
  }

  // Validate mask for phone and cellphone. Ex. 2121-9707 or 9 9999-9999
  phoneMask(phoneNumber: string) {
    if (phoneNumber.length <= 10) {
      return '0000-00000';
    }
    return '0 0000-0000';
  }

  public cnpjMask(event: any) {
    // Permite Backspace para usuário remover ")" e "-"
    if (event.keyCode === 8 && this.deleteChar(event.target.value)) {
      return;
    }
    event.target.value = this.maskCnpj(event.target.value);
  }

  public cpfMask(event: any) {
    // Permite Backspace para usuário remover ")" e "-"
    if (event.keyCode === 8 && this.deleteChar(event.target.value)) {
      return;
    }
    event.target.value = this.maskCpf(event.target.value);
  }

  public dateMask(event: any) {
    if (event.keyCode === 8 && this.deleteChar(event.target.value)) {
      return;
    }
    event.target.value = this.maskDate(event.target.value);
  }

  maskDate(value: any) {
    if (value) {
      value = value.replace(/\D/g, '');
      if (value.length <= 8) {
        value = value.replace(/(\d{2})(\d)/, '$1/$2');
        value = value.replace(/(\d{2})(\d{1,4})$/, '$1/$2');
      }
      return value;
    }
    return '';
  }

  deleteChar(value: any): boolean {
    if (value.length === 9 || value.length === 4 || value.length === 3) {
      return true;
    } else {
      return false;
    }
  }

  maskCnpj(value: any) {
    if (value) {
      value = value.replace(/\D/g, '');
      if (value.length <= 18) {
        value = value.replace(/^(\d{2})(\d)/, '$1.$2');
        value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
        value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
        value = value.replace(/(\d{4})(\d)/, '$1-$2');
      }
      return value;
    }
    return '';
  }

  maskCpf(value: any) {
    if (value) {
      value = value.replace(/\D/g, '');
      if (value.length <= 11) {
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      }
      return value;
    }
    return '';
  }

  public rgMask(event: any) {
    // Permite Backspace para usuário remover ")" e "-"
    if (event.keyCode === 8 && this.deleteChar(event.target.value)) {
      return;
    }
    event.target.value = this.maskRg(event.target.value);
  }
  maskRg(value: any) {
    if (value) {
      value = value.replace(/\D/g, '');
      if (value.length < 10) {
        value = value.replace(/(\d{1})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
      }
      return value;
    }
    return '';
  }

  public cepMask(event: any) {
    // Permite Backspace para usuário remover ")" e "-"
    if (event.keyCode === 8 && this.deleteChar(event.target.value)) {
      return;
    }
    event.target.value = this.maskCep(event.target.value);
  }

  maskCep(value: any) {
    if (value) {
      value = value.replace(/\D/g, '');
      if (value.length < 9) {
        value = value.replace(/(\d{5})(\d)/, '$1-$2');
      }
      return value;
    }
    return '';
  }

  public cpfCnpjMask(event) {
    // Permite Backspace para usuário remover ")" e "-"
    if (event.keyCode === 8 && this.deletandoCaracter(event.target.value)) {
      return;
    }
    event.target.value = this.maskCpfCnpj(event.target.value);
  }

  deletandoCaracter(value): boolean {
    if (value.length === 9 || value.length === 4 || value.length === 3) {
      return true;
    }
    return false;
  }

  maskCpfCnpj(value) {
    if (value) {
      value = value.replace(/\D/g, '');
      if (value.length <= 11) {
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      } else {
        value = value.replace(/^(\d{2})(\d)/, '$1.$2');
        value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
        value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
        value = value.replace(/(\d{4})(\d)/, '$1-$2');
      }
      return value;
    }
    return '';
  }

  equalsTo(otherField: string) {
    const validator = (formControl: FormControl) => {
      if (otherField == null) {
        throw new Error('É necessário informar um campo.');
      }
      if (!formControl.root || !(<FormGroup>formControl.root).controls) {
        return null;
      }
      const field = (<FormGroup>formControl.root).get(otherField);
      if (!field) {
        throw new Error('é necessário informar um campo válido.');
      }
      if (field.value !== formControl.value) {
        return { equalsTo: otherField };
      }
      return null;
    };
    return validator;
  }

  removeAccents(text: any) {
    text = text.replace(/\W/g, '');
    const accents = 'ÄÅÁÂÀÃäáâàãÉÊËÈéêëèÍÎÏÌíîïìÖÓÔÒÕöóôòõÜÚÛüúûùÇç';
    const noAccents = 'AAAAAAaaaaaEEEEeeeeIIIIiiiiOOOOOoooooUUUuuuuCc';
    for (let i = 0; i < accents.length; i++) {
      text = text.replace(accents[i].toString(), noAccents[i].toString());
    }
    return text;
  }

  transformObject(array: any) {
    let newArray = array.map((app) => {
      app.titleFake = this.removeAccents(app.title.toUpperCase());
    });
    return newArray;
  }

  dateValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value) {
      if (control.value.length == 8) {
        var day = Number(control.value.substring(0, 2));
        var month = Number(control.value.substring(2, 4));
        var year = Number(control.value.substring(4, 8));
      } else {
        return { dateInvalid: true };
      }
      if (month > 12 || day > 31) {
        return { dateInvalid: true };
      }
      if ((month === 4 || month === 6 || month === 9 || month === 11) && day === 31) {
        return { dateInvalid: true };
      }
      if (month == 2) {
        // regra para verificar se o ano é bissexto
        var isleap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
        if (day > 29 || (day === 29 && !isleap)) {
          return { dateInvalid: true };
        }
      }
      // regra para verificar se a data é maior que a data atual
      // var completeDate = month + '/' + day + '/' + year;
      // var dateActual = new Date(completeDate).getTime();
      // var currentDate = new Date().getTime();
      // if (dateActual > currentDate) {
      //   return { dateInvalid: true };
      // }
    }
    return null;
  }

  isFalsy(variable: any): boolean {
    if (typeof variable === 'boolean') return false;
    return (
      variable === undefined ||
      variable === null ||
      (Array.isArray(variable) && variable.length === 0) ||
      (typeof variable === 'object' && Object.keys(variable).length === 0 && variable.constructor === Object) ||
      (typeof variable === 'string' && variable.length === 0)
    );
  }

  filterDataForm(dataForm: any): any {
    Object.entries(dataForm).forEach((element) => {
      const key: string = element[0];
      const value: any = element[1];
      if (this.isFalsy(value)) {
        delete dataForm[key];
      }
    });
    return dataForm;
  }

  public verifyNullableFields(dataForm: any): any {
    let nullableFields: string = '';

    Object.entries(dataForm).forEach((element) => {
      const key: string = element[0];
      const value: any = element[1];

      if (
        (typeof value !== 'boolean' &&
          value !== 0 &&
          (value === null ||
            value === undefined ||
            (typeof value === 'object' && Object.keys(value).length === 0 && !(value instanceof Date)) ||
            value === '[]')) ||
        (typeof value === 'string' && value.length === 0)
      ) {
        if (key != 'nullableFields') {
          nullableFields += `${key}|`;
          delete dataForm[key];
        }
      }
    });

    if (nullableFields) dataForm.nullableFields = nullableFields.substring(0, nullableFields.length - 1);

    return dataForm;
  }

  validatePhoneNumber(control: AbstractControl) {
    var phoneNumber: any = Number(control.value);
    // phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    if (!phoneNumber) {
      return null;
    }
    var patternNumber = new RegExp(`^(?:[14689][1-9]|2[12478]|3[1234578]|5[1345]|7[134579])(?:[2-8])[0-9]{3}[0-9]{4}$`);
    if (patternNumber.test(phoneNumber)) {
      return null;
    }
    return { custom: true };
  }

  validateMobileNumber(control: AbstractControl) {
    var phoneNumber: any = Number(control.value);
    // phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    var patternNumber = new RegExp(`^(?:[14689][1-9]|2[12478]|3[1234578]|5[1345]|7[134579])(?:9[1-9])[0-9]{3}[0-9]{4}$`);
    if (patternNumber.test(phoneNumber)) {
      return null;
    }
    return { custom: true };
  }

  /**
   * Essa função faz o reset do formulário
   */
  resetForm(form: FormGroup) {
    form.reset();
  }

  verifyMediasHasPhone(control: AbstractControl) {
    var medias: [] = JSON.parse(control.value);
    const isPhone = (media) => media.id === 'phone';

    if (!medias) {
      return null;
    }

    if (medias.some(isPhone)) {
      return null;
    }

    return { custom: true };
  }

  /**
   * Essa função recebe um array de FormGroup, e percorre todos eles para marcar todos como "tocados / Touched"
   */
  checkValidateForms(forms: FormGroup[]) {
    // TODO: Modificar essa função para uma melhor opção, hoje ela está setando sua proxima aba como tocada.
    forms.forEach((formItem, index, arr) => {
      Object.keys(formItem.controls).forEach((field: string) => {
        let control = formItem.get(field);
        if (control instanceof FormControl) {
          control.markAsTouched({ onlySelf: true });
        }
      });
    });
  }

  autocompleteObjectValidator(control: AbstractControl): any {
    if (typeof control.value === 'string') {
      if (control.value.length === 0) return null;
      return { custom: { value: control.value } };
    }
    return null; /* valid option selected */
  }

  public getErrorMsg(fieldName: string, validatorName: string, validatorValue?: any, labelInput?: string) {
    const config = {
      required: `<b class="font-weight-bold-custom">${labelInput}</b> ${this.resource.getResource('@validations.required')}`,
      minlength: `<b class="font-weight-bold-custom">${labelInput}</b> ${this.resource.getResource('@validations.minLength')} ${
        validatorValue.minlength?.requiredLength
      } ${this.resource.getResource('@validations.characters')}`,
      maxlength: `<b class="font-weight-bold-custom">${labelInput}</b> ${this.resource.getResource('@validations.maxLength')} ${
        validatorValue.maxlength?.requiredLength
      } ${this.resource.getResource('@validations.characters')}`,
      custom: `${this.resource.getResource('@validations.report')} ${labelInput} ${this.resource.getResource(
        '@validations.valid'
      )}`,
      equalsTo: `<b class="font-weight-bold-custom">${labelInput}</b> ${this.resource.getResource('@validations.equalsTo')}`,
      pattern: `<b class="font-weight-bold-custom">${labelInput}</b> ${this.resource.getResource('@validations.pattern')}`,
      firedDate: `<b class="font-weight-bold-custom">${labelInput}</b> ${this.resource.getResource('@validations.firedDate')}`,
      fileInvalid: `<b class="font-weight-bold-custom">${labelInput}</b> ${this.resource.getResource('@validations.fileInvalid')}`
    };

    return config[validatorName];
  }

  getErrorCountFormControl(container: FormGroup, elementRef: ElementRef): any {
    let errorCount = 0;
    let contentErros = [];
    for (let controlKey in container.controls) {
      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
          let inputDocument = elementRef.nativeElement.querySelector(`#${controlKey}`);
          let labelInput = inputDocument.attributes['title'].value;
          contentErros.push(
            this.getErrorMsg(
              controlKey,
              Object.keys(container.controls[controlKey].errors)[0],
              container.controls[controlKey].errors,
              labelInput
            )
          );
        }
      }
    }

    let sendContent = {
      contentErros,
      errorCount
    };
    return sendContent;
  }

  showToast(
    layoutType: DialogLayoutDisplay,
    title: string,
    message: string,
    toastPosition: ToastPositionEnum = ToastPositionEnum.TOP_CENTER,
    autoCloseDelay = 5000,
    progressBar: ToastProgressBarEnum = ToastProgressBarEnum.INCREASE,
    toastUserViewType: ToastUserViewTypeEnum = ToastUserViewTypeEnum.SIMPLE,
    animationIn: AppearanceAnimation = AppearanceAnimation.BOUNCE_IN,
    animationOut: DisappearanceAnimation = DisappearanceAnimation.BOUNCE_OUT
  ) {
    const newToastNotification = new ToastNotificationInitializer();
    newToastNotification.setTitle(title);
    newToastNotification.setMessage(message);

    // Choose layout color type
    newToastNotification.setConfig({
      autoCloseDelay: autoCloseDelay, // optional
      textPosition: 'center', // optional
      layoutType: layoutType, // SUCCESS | INFO | NONE | DANGER | WARNING
      progressBar: progressBar, // INCREASE | DECREASE | NONE
      toastUserViewType: toastUserViewType, // STANDARD | SIMPLE
      animationIn: animationIn, // BOUNCE_IN | SWING | ZOOM_IN | ZOOM_IN_ROTATE | ELASTIC | JELLO | FADE_IN | SLIDE_IN_UP | SLIDE_IN_DOWN | SLIDE_IN_LEFT | SLIDE_IN_RIGHT | NONE
      animationOut: animationOut, // BOUNCE_OUT | ZOOM_OUT | ZOOM_OUT_WIND | ZOOM_OUT_ROTATE | FLIP_OUT | SLIDE_OUT_UP | SLIDE_OUT_DOWN | SLIDE_OUT_LEFT | SLIDE_OUT_RIGHT | NONE
      toastPosition: toastPosition // TOP_LEFT | TOP_CENTER | TOP_RIGHT | TOP_FULL_WIDTH | BOTTOM_LEFT | BOTTOM_CENTER | BOTTOM_RIGHT | BOTTOM_FULL_WIDTH
    });

    // Simply open the popup
    newToastNotification.openToastNotification$();
  }

  sanitize(base64?: string) {
    if (base64 != null) {
      if (base64.startsWith('/9j/4'))
        return this.sanitizer.bypassSecurityTrustResourceUrl('data:image/jpg;base64,' + base64 || '');
      if (base64.startsWith('iVBOR'))
        return this.sanitizer.bypassSecurityTrustResourceUrl('data:image/png;base64,' + base64 || '');
      if (base64.startsWith('UklGR'))
        return this.sanitizer.bypassSecurityTrustResourceUrl('data:image/octet-stream;base64,' + base64 || '');
      if (base64.startsWith('JVBER'))
        return this.sanitizer.bypassSecurityTrustResourceUrl('data:application/pdf;base64,' + base64 || '');

      return this.sanitizer.bypassSecurityTrustResourceUrl(base64 || '');
    }
    return null;
  }

  /**
   * Essa função retorna o dia atual, no formato YYYY-MM-DD
   * @returns {String} YYYY-MM-DD
   */
  getToday(): string {
    let now = new Date(Date.now());
    let today = now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate();
    return today;
  }

  /**
   * Essa função retorna o dia somado com o parâmetro informado, no formato YYYY-MM-DD
   * @param {String} date - Dia atual no formato de YYYY-MM-DD.
   * @param {Number} sumDay - Número de dias a serem somados.
   * @returns {String} YYYY-MM-DD
   */
  getSumDay(date: string, sumDay: number): string {
    let now = new Date(`${date} 00:00:00`);
    now.setDate(now.getDate() + sumDay);
    return (
      now.getFullYear() + '-' + (now.getMonth() + 1).toString().padStart(2, '0') + '-' + now.getDate().toString().padStart(2, '0')
    );
  }

  /**
   * Recebe um payload e remove os campos que não foram indicados, além de verificar se existem campos para serem anulados e adicionados ao nullableFields
   * @param payload Payload contendo os dados
   * @param fieldsChanged Uma lista de campos separada por pipe. Ex: nome|email|senha
   * @returns payload
   */
  removeUnchangedFields(payload: any, fieldsChanged: string): any {
    if (!fieldsChanged) return this.verifyNullableFields(payload);
    const fieldsToStay: string[] = fieldsChanged.split('|');
    payload = this.verifyNullableFields(payload);

    Object.entries(payload).forEach((element) => {
      const key: string = element[0];
      if (key === 'nullableFields' || key === 'id') return;

      for (let i = 0; i < fieldsToStay.length; i++) {
        if (key === fieldsToStay[i]) return;
      }
      delete payload[key];
    });

    return payload;
  }

  removeUnchangedFields2(payload: any, fieldsChanged: string): any {
    //if (!fieldsChanged) return this.verifyNullableFields(payload);
    const fieldsToStay: string[] = fieldsChanged.split('|');
    payload = this.verifyNullableFields(payload);

    Object.entries(payload).forEach((element) => {
      const key: string = element[0];
      if (key === 'nullableFields' || key === 'id') return;

      for (let i = 0; i < fieldsToStay.length; i++) {
        if (key === fieldsToStay[i]) return;
      }
      delete payload[key];
    });

    return payload;
  }

  /* 
  {
  "result": 400,
  "errors": [
    "Cannot find nested property 'numbers' on the resource type 'Direction.Phones'."
  ]
}
  */

  showErrorMessages(error: HttpErrorResponse): void {
    if (error.error?.notifications && error.error?.notifications[0]?.value) {
      error.error?.notifications.forEach((err) => {
        this.showToast(DialogLayoutDisplay.DANGER, 'Erro!', err.value);
      });
      return;
    }
    if (error.error?.notifications && error.error?.notifications[0]?.errors?.length) {
      this.showToast(DialogLayoutDisplay.DANGER, 'Erro!', error.error.notifications[0].errors[0].errorMessage);
      return;
    }
    if (error.error?.errors?.length) {
      error.error?.errors.forEach((err: string) => {
        this.showToast(DialogLayoutDisplay.DANGER, 'Erro!', err);
      });
      return;
    }
    this.showToast(DialogLayoutDisplay.DANGER, 'Erro!', JSON.stringify('Ocorreu um erro inesperado!'));
    return;
  }

  getImageById(id: string): Promise<any> {
    const headers = new HttpHeaders({ 'DS-AutoComplete-Request': 'True' });
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${environment.api}/exercises?$select=id,image&$filter=id eq ${id}`, { headers }).subscribe({
        next: (r) => resolve(r),
        error: (e) => reject(e)
      });
    });
  }
}
